import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { useParams, useLocation } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Tooltip as MuiTooltip } from '@mui/material'
import { useDispatch, useSelector } from '../../store'
import {
  OfferStatus,
  PrivateOffer,
  PrivateOffersState,
  PrivateOfferObject,
} from '../../modules/privateOffer/reducer'

import {
  CloudType,
  PartnerType,
  actionTypeWrapper,
  copyToClipboard,
  extractAccountIdFromCombinedId,
  getLabelFromMapping,
  getSelectedAccountIdWithCloud,
  isLoading,
  LoadingTypes,
  searchDropDownOptionMapping,
  searchDropDownOptions,
  setSelectedAccount,
  standardLabraDateFormat,
  startLoading,
  stopLoading,
  updateAppAlert,
  useAccountId,
  useCloudAccounts,
  useCurrentCloudProvider,
  useScreenSize,
  useSyncCloudMarketplace,
  useUserType,
  useValidateCloudRoute,
  providerToAuthCloud,
  CloudChangeV2,
  CloudAccountChangeV2,
  type CloudAccountChangeParams,
  SearchResultsCount,
} from '@labrav/common'
import {
  clearPrivateOffers,
  getPrivateOffers,
  patchOfferData,
  setPageNumber,
  updateOffer,
} from '../../modules/privateOffer/action'
import clsx from 'clsx'
import {
  ButtonV2,
  LabraToolTipV2,
  ColumnMenuOption,
  DropdownButtonV2,
  PlanType,
  ColumnActionWithMenuV2,
  ModalConfirmV2,
  ModalSizeVariants,
  Filters,
  SectionProps,
  CheckBoxFieldv2,
  RadioButtonsV2,
  FiltersApplied,
  StatusChipV4,
  useAuth,
  LoadingLogo,
} from '@labrav/react-components'
import { get, isEmpty } from 'lodash'
import { TextField, Typography } from '@mui/material'
import { ClassNameMap } from '@mui/styles'
import type { DetailedOfferPreviewData } from '../PrivateOffers/types'
import { DetailedOffer } from '../PrivateOfferForm/DetailedOffer/DetailedOffer'
import {
  CloudMarketplace,
  FlyoutFunction,
  Product,
  ProductStatus,
} from '../../modules/productsListing/types'
import {
  getProducts,
  setSelectedProductOnListing,
  clearProductsListing,
} from '../../modules/productsListing/action'
import {
  setCloudMarketplaceProductListing,
  setCreatePrivateOfferModalOpen,
} from '../../modules/productListingFilter/action'
import { useStyles } from './PrivateOffersWrapperUIV2.styles'
import PrivateOffersV2 from '../PrivateOffersV2/PrivateOffersV2'
import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { DateTime as DateTimeLuxon } from 'luxon'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { ProductSelectorDropdown } from '../../common/components/ProductSelectorDropdown/ProductSelectorDropdown'
import { SearchBoxComponent } from '@labrav/react-components'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import { SubscriptionSelectionModal } from '../Subscription/SubscriptionSelectionModal/SubscriptionSelectionModal'
import { ModalSubscriptionsState } from '../../modules/modalSubscription/reducer'
import { ModalToCreatePrivateOffer } from '../../common/components/ModalToCreatePrivateOffer/ModalToCreatePrivateOffer'
import {
  awsPrivateOfferStatus,
  expiryDateMapping,
  microsoftPrivateOfferStatus,
} from '../../utils/privateOffersFilterMapping'
import { getActiveReceivedResaleAuthorizations } from '../../modules/subscription/subscriptionsListDeps'
import { ResponsiveColumn } from '../../common/components/ResponsiveColumn/ResponsiveColumn'
import { deleteCurrentPrivateOffer } from '../../modules/privateOffer/action'
import { getDeleteOfferDisabledTooltip } from '../../utils/getDeleteOfferDisabledTooltip'
import { getStatusAndLabelForPrivateOffer } from '../../common/utils/getStatusAndLabelForPrivateOffer'
import { resolveLabraProductIdForPrivateOfferNavigation } from '../../utils/privateOfferNavigation'
import {
  getExpirationDateFromPrivateOffer,
  getKeyCloudAccount,
  getOfferRenewalType,
  getPrivateOfferRedirectUrl,
  getProductIdFromPrivateOffer,
  getStatusOffer as getStatusOfferBase,
} from '../../utils/privateOffersListHelpers'
import { ChannelOfferCreationModal } from '../ChannelOffers/ChannelOfferCreationModal'
import { usePrivateOfferListTracking } from '../../common/utils/hooks/usePrivateOfferCreationTracking'

const getStatusOffer = (
  offer: Parameters<typeof getStatusOfferBase>[0],
  cloudType: Parameters<typeof getStatusOfferBase>[1]
) => getStatusOfferBase(offer, cloudType, { skipAzureExpiryStatus: true })

export type PrivateOfferListTrackingHandlers = {
  trackSearch: (params: { searchQuery: string; searchField: string }) => void
  trackFiltersApplied: (params: {
    expiringInDays: string
    offerStatuses: string[]
    appliedFilterLabels: string[]
  }) => void
  trackFilterRemoved: (params: {
    removedFilter: string
    remainingFilterLabels: string[]
  }) => void
  trackRowAction: (params: {
    actionKey: string
    privateOfferId?: string
    cloudType?: string
    offerStatus?: string
  }) => void
}

type PrivateOffersActionsLabraUI = {
  classes: ClassNameMap
  offer: PrivateOffer
  productId: string
  externalBaseURL?: string
  search?: string
  cloudType: CloudMarketplace
  trackRowAction?: PrivateOfferListTrackingHandlers['trackRowAction']
}

const noopTrackRowAction: PrivateOfferListTrackingHandlers['trackRowAction'] =
  () => undefined

export const PrivateOffersActionsLabraUI = React.memo(
  ({
    offer,
    productId,
    externalBaseURL,
    search,
    cloudType,
    trackRowAction = noopTrackRowAction,
  }: PrivateOffersActionsLabraUI) => {
    const navigate = useNavigate()
    const { classes } = useStyles({})
    const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
      cloud?: string
      accountId?: string
    }>()
    const [copied, setCopied] = useState<boolean>(false)
    const dispatch = useDispatch()

    const offerLink =
      cloudType === 'AZURE' ? offer?.acceptanceLink : offer.labraOfferUrl

    const handleSelectOption = async (option: ColumnMenuOption) => {
      trackRowAction({
        actionKey: option.key,
        privateOfferId: offer.privateOfferId || offer.id,
        cloudType,
        offerStatus: offer.offerStatus,
      })
      if (option.key === 'Copy-private-offer-URL') {
        if (!offerLink) {
          console.error('No offer link found')
          return
        }
        try {
          await copyToClipboard(offerLink)
          setCopied(true)
          setTimeout(() => {
            setCopied(false)
          }, 2000)
        } catch (error) {
          console.error('Error copying URL to clipboard:', error)
        }
      } else if (option.key === 'Delete-offer') {
        handleOpenDeleteModal()
      } else if (option?.route) {
        navigate({
          pathname: option.route,
          search: search,
        })
      }
    }
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const handleOpenDeleteModal = () => {
      setOpenDeleteModal(prev => (prev ? prev : true))
    }
    const handleCloseDeleteModal = () => {
      setOpenDeleteModal(false)
    }
    const deleteLoading = useSelector<boolean>(state =>
      isLoading(state.loading)(LoadingTypes.DELETE_OFFER)
    )
    const handleConfirmDeleteModal = async () => {
      await dispatch(
        deleteCurrentPrivateOffer(
          offer.privateOfferId,
          () => setOpenDeleteModal(false),
          routeAccountId,
          cloudType
        )
      )

      navigate({
        pathname: externalBaseURL
          ? externalBaseURL
          : routeCloud && routeAccountId
            ? `/flyout/${routeCloud}/${routeAccountId}/offers-authorizations/private-offers`
            : `/flyout/offers-authorizations/private-offers`,
        search,
      })
    }
    const offerLinkTooltipPlacement = copied
      ? 'top'
      : offer.offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
        ? 'top-end'
        : 'top'

    const redirectURL = getPrivateOfferRedirectUrl({
      cloudType,
      offer,
      externalBaseURL,
      productId,
      cloud: routeCloud?.toLowerCase(),
      accountId: routeAccountId,
    })

    const { loading: authLoading, canDo } = useAuth()
    const canDeletePrivateOffer = useMemo(() => {
      return (
        !authLoading &&
        canDo({
          cloud: providerToAuthCloud(cloudType),
          service: 'marketplace',
          action: 'create',
        })
      )
    }, [authLoading, canDo, cloudType])

    const permissionMessageString =
      'This action is disabled because you have read-only access'

    const viewOfferOption: ColumnMenuOption = {
      key: 'View-offer-details',
      label: 'View offer details',
      route: redirectURL,
    }

    return (
      <div
        className={classes.actionColumn}
        data-prevent-row-click
        data-testid={'actions'}
      >
        <ColumnActionWithMenuV2
          handleSelectOption={handleSelectOption}
          menuOptions={[
            ...(cloudType === 'AZURE' || cloudType === 'GCP'
              ? [viewOfferOption]
              : []),
            {
              key: 'Copy-private-offer-URL',
              label: (
                <LabraToolTipV2
                  description={
                    copied
                      ? 'Copied'
                      : offer.offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
                        ? 'This offer has expired. Extend the offer expiry date and then copy the offer link.'
                        : ''
                  }
                  tooltipPlacement={offerLinkTooltipPlacement}
                  icon={
                    copied &&
                    offer.offerStatus !== OfferStatus.PRIVATE_OFFER_EXPIRED ? (
                      <FontAwesomeIcon
                        icon={faCheck as IconProp}
                        style={{ width: '12px', height: '12px' }}
                      />
                    ) : undefined
                  }
                  tooltipPlacmentGap="0"
                >
                  Copy private offer URL
                </LabraToolTipV2>
              ),
              disable: !(
                [
                  OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
                  OfferStatus.PRIVATE_OFFER_VIEWED,
                  OfferStatus.PRIVATE_OFFER_ACCEPTED,
                ].includes(offer?.offerStatus as OfferStatus) &&
                !isEmpty(offerLink)
              ),
              disabledTooltip:
                offer?.offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
                  ? 'This offer has expired. Extend the offer expiry date and then copy the offer link.'
                  : `The Channel Offer URL is not yet ready. It is in progress at ${cloudType}.`,
            },
            ...(cloudType === 'AWS' ? [viewOfferOption] : []),
            {
              key: 'Delete-offer',
              label: !canDeletePrivateOffer ? (
                <MuiTooltip title={permissionMessageString}>
                  <span>Delete offer</span>
                </MuiTooltip>
              ) : (
                'Delete offer'
              ),
              disable:
                !canDeletePrivateOffer ||
                !(
                  offer?.offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED ||
                  offer?.draft === true ||
                  offer?.offerStatus === OfferStatus.PRIVATE_OFFER_FAILED ||
                  offer?.offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED
                ),
              disabledTooltip: !canDeletePrivateOffer
                ? permissionMessageString
                : getDeleteOfferDisabledTooltip({
                    privateOfferId: offer?.privateOfferId || offer?.id || '',
                    draft: offer?.draft,
                    offerStatus: offer?.offerStatus,
                  } as PrivateOffer),
            },
          ]}
        />

        <ModalConfirmV2
          title={'Delete this private offer? '}
          description={
            <Typography>
              <p>
                You will not be able to recover this offer or any information
                inside it once this offer is deleted.
              </p>

              <p>Are you sure you want to delete this offer?</p>
            </Typography>
          }
          open={openDeleteModal}
          titleAcceptButton={'Delete'}
          onAccept={handleConfirmDeleteModal}
          acceptLoading={deleteLoading}
          onDecline={handleCloseDeleteModal}
          variant={ModalSizeVariants.small}
          icon={
            <FontAwesomeIcon
              icon={faTrash}
              style={{ width: '14px', height: '16px' }}
            />
          }
          hideCancelIcon={false}
          hideCancelButton={false}
        />
      </div>
    )
  }
)

export type PrivateOffersWrapperV2Props = {
  crmOpportunityId?: string
  externalBaseURL?: string
  search?: string
  tableEl?: React.RefObject<HTMLDivElement>
}
const PrivateOffersWrapperUIV2: React.FC<PrivateOffersWrapperV2Props> = ({
  externalBaseURL,
  search,
  crmOpportunityId,
  tableEl,
}) => {
  const [searchNow, setSearchNow] = useState(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState('Company')
  const { classes } = useStyles({ isExternal: !!externalBaseURL, searchNow })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const privateOfferListTracking = usePrivateOfferListTracking()

  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const {
    products,
    selectedProduct: productId,
    additionalAllowedPrivateOffers,
    plan,
  } = useFlyOutProductsBasedOnSelectedCloud()

  const privateOfferStatus =
    selectedCloudTypeProductListing === 'AWS'
      ? awsPrivateOfferStatus
      : microsoftPrivateOfferStatus

  const noopListTracking: PrivateOfferListTrackingHandlers = useMemo(
    () => ({
      trackSearch: () => undefined,
      trackFiltersApplied: () => undefined,
      trackFilterRemoved: () => undefined,
      trackRowAction: () => undefined,
    }),
    []
  )
  const {
    trackSearch,
    trackFiltersApplied,
    trackFilterRemoved,
    trackRowAction,
  } = privateOfferListTracking ?? noopListTracking

  const selectedProductDetails = useMemo(() => {
    return (
      products.find(product => product.productId === productId) ||
      ({} as Product)
    )
  }, [products, productId])

  const productsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [openNewPrivateOfferModal, setOpenNewPrivateOfferModal] =
    useState<boolean>(false)
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [expiryDate, setExpiryDate] = useState<string>('')
  const [
    selectedPrivateOfferStatusCheckboxes,
    setSelectedPrivateOfferStatusCheckboxes,
  ] = useState<string[]>([])
  const [appliedFilteredValues, setAppliedFilteredValues] = useState<string[]>(
    []
  )
  const { subscriptions } = useSelector<ModalSubscriptionsState>(
    state => state.modalSubscription
  )
  const selectedActiveReceivedResellerAuthorization = useSelector<string>(
    state =>
      (
        state as {
          receivedResellerAuthorization?: {
            selectedActiveReceivedResellerAuthorization?: string
          }
        }
      )?.receivedResellerAuthorization
        ?.selectedActiveReceivedResellerAuthorization || ''
  )
  const loadingActiveResellerAuth = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.ACTIVE_RESELLER_AUTHORIZATION)
  )

  const [selectedSubscription, setSelectedSubscription] = useState<string>('')
  const [cppoSubscription, setCppoSubscription] = useState<string>('')
  const handleClick = () => {
    setIsModalOpen(true)
  }
  const handleModalClose = () => {
    setIsModalOpen(false)
  }

  const acceptSelectedSubscription = async (subscriptionId: string) => {
    const selectedSubscriptionItem = subscriptions.find(
      subscriptionItem => subscriptionItem.subscriptionId === subscriptionId
    )
    if (selectedSubscriptionItem?.offerType === 'CPPO') {
      await dispatch(
        getActiveReceivedResaleAuthorizations({
          partnerId: currentAccountId,
          subscriptionId,
        })
      )
      await setSelectedSubscription(subscriptionId)
      await setCppoSubscription('create')
    } else {
      setIsModalOpen(false)
      setSelectedSubscription(subscriptionId)
      // Construct subscription URL with cloud/accountId if available
      const subscriptionPath = externalBaseURL
        ? `${externalBaseURL?.replace('/product-listing', '') || '/flyout'}/subscriptions/${subscriptionId}/edit`
        : routeCloud && currentAccountId
          ? `/flyout/${routeCloud}/${currentAccountId}/subscriptions/${subscriptionId}/edit`
          : `/flyout/subscriptions/${subscriptionId}/edit`

      navigate(
        {
          pathname: subscriptionPath,
          search: search,
        },
        {
          state: {
            subscriptionId: subscriptionId,
          },
        }
      )
    }
  }
  const handleNext = (subscriptionId?: string) => {
    // Construct CPPO subscription URL with cloud/accountId if available
    const cppoSubscriptionPath =
      routeCloud && currentAccountId
        ? `/flyout/${routeCloud}/${currentAccountId}/subscriptions/${subscriptionId}/authorization/${selectedActiveReceivedResellerAuthorization}/edit`
        : `/flyout/subscriptions/${subscriptionId}/authorization/${selectedActiveReceivedResellerAuthorization}/edit`

    navigate(cppoSubscriptionPath)
  }

  const onDeleteFiltersAppliedChip = useCallback(
    async (value: string) => {
      if (value.startsWith('Expiring in')) {
        const remainingFilterLabels = Object.keys(privateOfferStatus).filter(
          status =>
            selectedPrivateOfferStatusCheckboxes.includes(
              privateOfferStatus[status]
            )
        )
        trackFilterRemoved({
          removedFilter: value,
          remainingFilterLabels,
        })
        setExpiryDate('')
        await dispatch(clearPrivateOffers())
        dispatch(
          getPrivateOffers({
            partnerId: currentAccountId,
            cloudMarketplace: selectedCloudTypeProductListing,
            pageSize: pageSizeValue,
            pageNumber: 1,
            productId,
            expiringInDays: '',
            offerStatus: selectedPrivateOfferStatusCheckboxes,
          })
        )
        setAppliedFilteredValues([...[], ...remainingFilterLabels])
      } else {
        setSelectedPrivateOfferStatusCheckboxes(prev => {
          const updatedStatusCheckboxes = prev.filter(
            status => status !== privateOfferStatus[value]
          )
          const remainingFilterLabels = [
            ...(expiryDate ? [`Expiring in ${expiryDate} days`] : []),
            ...Object.keys(privateOfferStatus).filter(status =>
              updatedStatusCheckboxes.includes(privateOfferStatus[status])
            ),
          ]
          trackFilterRemoved({
            removedFilter: value,
            remainingFilterLabels,
          })
          dispatch(clearPrivateOffers())
          dispatch(
            getPrivateOffers({
              partnerId: currentAccountId,
              cloudMarketplace: selectedCloudTypeProductListing,
              pageSize: pageSizeValue,
              pageNumber: 1,
              productId,
              expiringInDays: expiryDate,
              offerStatus: updatedStatusCheckboxes,
            })
          )
          setAppliedFilteredValues(remainingFilterLabels)
          return updatedStatusCheckboxes
        })
      }
    },
    [
      expiryDate,
      selectedPrivateOfferStatusCheckboxes,
      trackFilterRemoved,
      privateOfferStatus,
    ]
  )

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(e.target.value)
  }

  const handleDeleteCheckbox = (title: keyof typeof privateOfferStatus) => {
    removeCheckbox(title)
  }

  const selectedCheckboxLabels = useMemo<string[]>(
    () =>
      Object.keys(privateOfferStatus).filter(key =>
        selectedPrivateOfferStatusCheckboxes.includes(privateOfferStatus[key])
      ),
    [selectedPrivateOfferStatusCheckboxes]
  )

  const expiryDateLabel = useMemo(
    () => getLabelFromMapping(expiryDateMapping, expiryDate),
    [expiryDate]
  )

  const handleCheckboxChange =
    (title: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target
      const valueToAddOrRemove = privateOfferStatus[title]

      setSelectedPrivateOfferStatusCheckboxes(prev => {
        const currentSet = new Set(prev)
        if (checked) {
          currentSet.add(valueToAddOrRemove)
        } else {
          currentSet.delete(valueToAddOrRemove)
        }

        return Array.from(currentSet)
      })
    }

  const removeCheckbox = (title: keyof typeof privateOfferStatus) => {
    const valueToRemove = privateOfferStatus[title]
    setSelectedPrivateOfferStatusCheckboxes(prev =>
      prev.filter(item => item !== valueToRemove)
    )
  }

  const sections: SectionProps[] = [
    {
      title: 'Expiry date',
      type: 'radio',
      content: (
        <div data-testid="expiry-date-radio" className={classes.filterSection}>
          <RadioButtonsV2
            checkedValue={{
              key: expiryDate || '',
              value: expiryDate || '',
            }}
            name="expiryDate"
            radioButtonMappings={expiryDateMapping}
            handleChange={handleExpiryDateChange}
            vertical={true}
            gapBetween={'0'}
          />
        </div>
      ),
      selectedValues: expiryDateLabel || undefined,
      onDelete: () => setExpiryDate(''),
    },
    {
      title: 'Status',
      type: 'checkbox',
      content: (
        <div
          data-testid="private-offer-status-checkbox"
          className={classes.filterSection}
        >
          {Object.keys(privateOfferStatus).map(title => (
            <div className={classes.filterCheckbox}>
              <CheckBoxFieldv2
                key={title}
                keyName="privatefferStatus"
                title={title}
                value={selectedPrivateOfferStatusCheckboxes.includes(
                  privateOfferStatus[title]
                )}
                onChangeBox={handleCheckboxChange(title)}
              />
            </div>
          ))}
        </div>
      ),
      selectedValues: selectedCheckboxLabels,
      onDelete: handleDeleteCheckbox,
    },
  ]

  const applyFilters = useCallback(async () => {
    const appliedFilterLabels = [
      ...(expiryDate ? [`Expiring in ${expiryDate} days`] : []),
      ...Object.keys(privateOfferStatus).filter(status =>
        selectedPrivateOfferStatusCheckboxes.includes(
          privateOfferStatus[status]
        )
      ),
    ]

    trackFiltersApplied({
      expiringInDays: expiryDate,
      offerStatuses: selectedPrivateOfferStatusCheckboxes,
      appliedFilterLabels,
    })

    await dispatch(clearPrivateOffers())
    dispatch(
      getPrivateOffers({
        partnerId: currentAccountId,
        cloudMarketplace: selectedCloudTypeProductListing,
        pageSize: pageSizeValue,
        pageNumber: 1,
        productId,
        expiringInDays: expiryDate,
        offerStatus: selectedPrivateOfferStatusCheckboxes,
      })
    )
    setAppliedFilteredValues(appliedFilterLabels)
  }, [
    expiryDate,
    selectedPrivateOfferStatusCheckboxes,
    trackFiltersApplied,
    privateOfferStatus,
  ])

  const handleOpenPrivateOfferModal = useCallback(() => {
    setOpenNewPrivateOfferModal(true)
  }, [])
  const handleClosePrivateOfferModal = useCallback(() => {
    setOpenNewPrivateOfferModal(false)
  }, [])

  // store modal open close state in redux
  useEffect(() => {
    if (externalBaseURL) {
      dispatch(setCreatePrivateOfferModalOpen(openNewPrivateOfferModal))
      return () => {
        dispatch(setCreatePrivateOfferModalOpen(false))
      }
    }
  }, [openNewPrivateOfferModal, dispatch])

  const handleProductChange = useCallback(
    (productId: string) => {
      setSelectedProductId(productId)
    },
    [productId]
  )

  const isFreeTier = plan && plan === 'free_tier'
  const enabledTocreate = isFreeTier
    ? additionalAllowedPrivateOffers !== undefined &&
      additionalAllowedPrivateOffers > 0
    : true // Skip validation for non-free tier plans

  const handleSearchClick = () => {
    if (inputValue.trim() !== '') {
      const searchField =
        (
          searchDropDownOptionMapping.privateOffersOptionMapping as Record<
            string,
            string
          >
        )[selectedOption] || ''

      trackSearch({
        searchQuery: inputValue,
        searchField,
      })

      setSearchNow(true)
      setShowCrossMark(true)
      dispatch(clearPrivateOffers())
      dispatch(
        getPrivateOffers({
          partnerId: currentAccountId,
          cloudMarketplace: selectedCloudTypeProductListing,
          pageSize: pageSizeValue,
          pageNumber: 1,
          productId,
          input: inputValue,
          selectedInput: searchField,
          sectionName: 'offers',
        })
      )
    }
  }

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const { totalCount } = useSelector<PrivateOffersState>(
    state => state.privateOffers
  )
  const privateOffers = useSelector<PrivateOfferObject>(
    state => state.privateOffers.privateOffers
  )

  //TO DO: https://ibexlabs.atlassian.net/browse/LT-3594 Remove this useEffect and its processing
  const {
    partner_id: accountIdFromParams,
    cloud: routeCloud,
    accountId: routeAccountId,
    secureKey: secureKeyFromURL,
    partnerId: partnerIdFromURL,
  } = useParams<{
    partner_id?: string
    cloud?: string
    accountId?: string
    secureKey?: string
    partnerId?: string
  }>()

  // Sync Redux cloud marketplace state with useCloud value
  useSyncCloudMarketplace()

  // Validate cloud type and redirect if unsupported (e.g., RedHat -> AWS)
  useValidateCloudRoute('flyout', '/flyout', !!externalBaseURL)

  const isAdmin = useUserType() === PartnerType.Admin
  const { canDo, loading: authLoading } = useAuth()

  const accountIdFromSelection = useAccountId({ product: 'flyout' }) || ''
  const currentAccountId =
    routeAccountId || accountIdFromParams || accountIdFromSelection
  const location = useLocation()

  // Get marketplace cloud accounts with unique IDs
  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')
  const marketplaceCloudAccounts = useMemo(() => {
    if (authLoading) {
      return baseMarketplaceCloudAccounts.map(account => ({ ...account }))
    }
    // Private offers list: allow browsing when user has marketplace *read* on that cloud.
    return baseMarketplaceCloudAccounts.map(account => ({
      ...account,
      disabled: !canDo({
        cloud: providerToAuthCloud(account.provider),
        service: 'marketplace',
        action: 'view',
      }),
    }))
  }, [authLoading, baseMarketplaceCloudAccounts, canDo])

  // Get the selected account ID with cloud for matching in dropdown
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    currentAccountId,
    'flyout',
    routeCloud || selectedCloudTypeProductListing
  )

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      // Extract original accountId from the combined format "accountId-product-cloud"
      const accountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params

      const cloudPath = cloud.toLowerCase()
      const pathname = externalBaseURL
        ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${cloudPath}/accounts/${accountId}/product-listing/offers-authorizations/private-offers`
        : `/flyout/${cloudPath}/${accountId}/offers-authorizations/private-offers`
      navigate({ pathname, search: location.search }, { replace: true })

      setInputValue('')
      setShowCrossMark(false)
      setSearchNow(false)
      setExpiryDate('')
      setSelectedPrivateOfferStatusCheckboxes([])
      setAppliedFilteredValues([])

      await dispatch(startLoading(LoadingTypes.GENERAL))

      await dispatch(
        setSelectedAccount(accountId, 'flyout', cloud as CloudType)
      )

      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )

      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))

      await dispatch(clearPrivateOffers())

      await dispatch(getProducts(accountId, cloud))

      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [
      dispatch,
      navigate,
      selectedCloudTypeProductListing,
      externalBaseURL,
      secureKeyFromURL,
      partnerIdFromURL,
      location.search,
    ]
  )

  const getProductsAndClearOffers = useCallback(async () => {
    await dispatch(clearPrivateOffers())
    await dispatch(
      getProducts(currentAccountId, selectedCloudTypeProductListing)
    )
  }, [currentAccountId, selectedCloudTypeProductListing])

  useEffect(() => {
    if (!isEmpty(currentAccountId) && !externalBaseURL) {
      if (isEmpty(products)) {
        getProductsAndClearOffers()
      }
    }
  }, [currentAccountId, selectedCloudTypeProductListing])
  const { pageSize: pageSizeValue, screenWidth } = useScreenSize()
  const columnMaxWidth = useMemo(
    () => `${(screenWidth * 16) / 100}px`,
    [screenWidth]
  )
  const columnMaxWidthOfferId = useMemo(
    () => `${(screenWidth * 5) / 100}px`,
    [screenWidth]
  )
  const columnMaxWidthCompanyName = useMemo(
    () => `${(screenWidth * 12) / 100}px`,
    [screenWidth]
  )

  useEffect(() => {
    if (
      !externalBaseURL &&
      productId !== undefined &&
      !isEmpty(products) &&
      !productsLoading
    ) {
      if (!isEmpty(inputValue) && !isEmpty(selectedOption)) {
        dispatch(
          getPrivateOffers({
            partnerId: currentAccountId,
            cloudMarketplace: selectedCloudTypeProductListing,
            pageSize: pageSizeValue,
            pageNumber: 1,
            productId,
            crmOpportunityId,
            input: inputValue,
            selectedInput:
              (
                searchDropDownOptionMapping.privateOffersOptionMapping as Record<
                  string,
                  string
                >
              )[selectedOption] || '',
            sectionName: 'offers',
          })
        )
      } else {
        dispatch(
          getPrivateOffers({
            partnerId: currentAccountId,
            cloudMarketplace: selectedCloudTypeProductListing,
            pageSize: pageSizeValue,
            pageNumber: 1,
            productId,
            expiringInDays: expiryDate,
            offerStatus: selectedPrivateOfferStatusCheckboxes,
          })
        )
      }
    }
    return () => {
      dispatch(
        updateAppAlert({
          message: undefined,
          messageType: 'INFO',
          autoClose: true,
        })
      )

      dispatch(clearPrivateOffers())
      dispatch(setPageNumber(0))
    }
  }, [
    currentAccountId,
    productId,
    searchNow,
    selectedCloudTypeProductListing,
    productsLoading,
  ])

  const handleInputChange = useCallback(
    (key: string, privateOfferId: string) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        dispatch(updateOffer(key, event.target.value, privateOfferId))
      },
    []
  )
  const [selectedPrivateOffer, setSelectedPrivateOffer] = useState<{
    offer: DetailedOfferPreviewData
    planType: PlanType
  } | null>(null)
  const [openModal, setOpenModal] = useState<boolean>(false)

  const viewDetailedOffer = useCallback(
    (privateOffer: PrivateOffer) => () => {
      const planType =
        products.find(
          product => product.productId === privateOffer?.awsProductId
        )?.productType || PlanType.SAAS_CONTRACT
      const offer = {
        ...privateOffer,
        offerName: privateOffer.privateOfferName,
      } as DetailedOfferPreviewData
      setSelectedPrivateOffer({ offer, planType })
      setOpenModal(true)
    },
    [products]
  )
  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [openModal])
  const updateOfferData = useCallback(
    (partnerId: string, privateOfferId: string) => () => {
      dispatch(
        patchOfferData(currentAccountId, privateOfferId, crmOpportunityId)
      )
    },
    []
  )

  const getOfferIdFieldForAdmin = (
    offer: PrivateOffer,
    cloud: CloudMarketplace
  ) => {
    switch (cloud) {
      case 'AWS': {
        return (
          <TextField
            onChange={handleInputChange('awsOfferId', offer.privateOfferId)}
            key={'awsOfferId'}
            id={`offer-id-${offer.privateOfferId}`}
            label={'Enter AWS offer ID'}
            placeholder="N/A"
            variant="standard"
            defaultValue={offer.awsOfferId}
            className={`${classes.resizeFontTextField} ${classes.defaultValueWidth}`}
            slotProps={{
              htmlInput: {
                'data-testid': `offer-id-${offer.privateOfferId}`,
                'data-id': `offer-id-${offer.privateOfferId}`,
              },
            }}
          />
        ) as never
      }
      case 'AZURE': {
        return <></>
      }
      case 'GCP': {
        return <></>
      }
      case 'REDHAT': {
        return <></>
      }
    }
  }

  const getAdminObjectsForTable = () =>
    Object.values(privateOffers).map(offer => {
      const keyNameForCloudOfferId = getKeyCloudAccount(
        selectedCloudTypeProductListing
      )
      const valueForCloudOfferId = getOfferIdFieldForAdmin(
        offer,
        selectedCloudTypeProductListing
      )
      return {
        privateOfferName: offer.privateOfferName,
        id: offer.privateOfferId,
        companyName: (
          <ResponsiveColumn
            linkText={offer.companyName}
            maxWidth={columnMaxWidth}
          />
        ),
        crmOrderObjectId: offer.crmOrderObjectId,
        key: offer.privateOfferId,
        createdAt: (!isEmpty(offer.createdAt)
          ? DateTimeLuxon.fromISO(offer.createdAt).toFormat(
              standardLabraDateFormat
            )
          : '-') as never,
        productName: (
          <ResponsiveColumn
            linkText={offer.productName || ''}
            maxWidth={columnMaxWidth}
          />
        ),
        [keyNameForCloudOfferId]: (
          <ResponsiveColumn
            linkText={valueForCloudOfferId}
            maxWidth={columnMaxWidthOfferId}
          />
        ),
        cloudOfferUrl: (
          <TextField
            onChange={handleInputChange('cloudOfferUrl', offer.privateOfferId)}
            id={`offer-url-${offer.privateOfferId}`}
            label={'Enter AWS offer URL'}
            placeholder="N/A"
            variant="standard"
            defaultValue={offer.cloudOfferUrl}
            className={`${classes.resizeFontTextField} ${classes.defaultValueWidth}`}
            slotProps={{
              htmlInput: {
                'data-testid': `offer-url-${offer.privateOfferId}`,
                'data-id': `offer-url-${offer.privateOfferId}`,
              },
            }}
          />
        ),
        actions: (
          <div className={classes.buttonContainer}>
            <ButtonV2
              onClick={updateOfferData(currentAccountId, offer.privateOfferId)}
              className={classes.submitButton}
            >
              Submit
            </ButtonV2>
            <ButtonV2
              onClick={viewDetailedOffer(offer)}
              className={classes.submitButton}
            >
              Details
            </ButtonV2>
          </div>
        ) as never,
      }
    })

  const getObjectsForTable = () =>
    Object.values(privateOffers).map(offer => {
      const keyNameForCloudOfferId = getKeyCloudAccount(
        selectedCloudTypeProductListing
      )
      const valueForCloudOfferId = get(offer, `${keyNameForCloudOfferId}`, '')
      const getStatus = getStatusOffer(offer, selectedCloudTypeProductListing)
      const statusDetails = getStatusAndLabelForPrivateOffer(
        getStatus,
        selectedCloudTypeProductListing
      )
      return {
        id: offer?.privateOfferId,
        companyName: (
          <ResponsiveColumn
            linkText={offer.companyName}
            maxWidth={columnMaxWidthCompanyName}
          />
        ),
        key: offer.privateOfferId,
        privateOfferName: (
          <ResponsiveColumn
            linkText={offer.privateOfferName}
            maxWidth={columnMaxWidth}
          />
        ),
        productName: (
          <ResponsiveColumn
            linkText={offer.productName || '-'}
            maxWidth={columnMaxWidth}
          />
        ),
        offerStatus: isEmpty(offer?.offerStatus) ? (
          '-'
        ) : (
          <StatusChipV4 {...statusDetails} />
        ),
        createdAt: (!isEmpty(offer.createdAt)
          ? DateTimeLuxon.fromISO(offer.createdAt).toFormat(
              standardLabraDateFormat
            )
          : '-') as never,
        ...(selectedCloudTypeProductListing === 'AWS'
          ? { type: getOfferRenewalType(offer.type) }
          : {}),
        offerExpirationDate: getExpirationDateFromPrivateOffer(
          offer,
          selectedCloudTypeProductListing,
          selectedCloudTypeProductListing === 'AZURE'
            ? !!offer?.offerStatus &&
                [
                  OfferStatus.OFFER_EMAIL_SENT,
                  OfferStatus.OFFER_VIEWED,
                ].includes(offer?.offerStatus as OfferStatus)
            : true
        ),
        [keyNameForCloudOfferId]: (
          <ResponsiveColumn
            linkText={valueForCloudOfferId || '-'}
            maxWidth={columnMaxWidthOfferId}
          />
        ),
        actions: (
          <PrivateOffersActionsLabraUI
            externalBaseURL={externalBaseURL}
            search={search}
            classes={classes as ClassNameMap}
            offer={offer}
            productId={resolveLabraProductIdForPrivateOfferNavigation({
              products,
              selectedProductId: productId,
              offerProductRef: getProductIdFromPrivateOffer(
                offer,
                selectedCloudTypeProductListing
              ),
            })}
            cloudType={selectedCloudTypeProductListing}
            trackRowAction={trackRowAction}
          />
        ) as never,
      }
    })
  const privateOffersForTable = isAdmin
    ? getAdminObjectsForTable()
    : getObjectsForTable()

  const handleRoute = (selected: ColumnMenuOption) => {
    if (selected.key === 'offer') {
      handleOpenPrivateOfferModal()
    } else if (selected.key === 'subscription') {
      handleClick()
    } else if (selected?.route) {
      navigate(selected?.route)
    }
  }
  const canCreatePrivateOffer = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(selectedCloudTypeProductListing),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, selectedCloudTypeProductListing])

  const permissionMessageString =
    'This action is disabled because you have read-only access'
  const createButtonWithSubscription =
    isFreeTier && !enabledTocreate ? (
      <LabraToolTipV2
        tooltipPlacement="bottom"
        description={
          'You have reached the creation limit in the free tier plan'
        }
      >
        <ButtonV2
          variant="contained"
          styleType="primary"
          disabled
          data-testid="create-private-offer"
        >
          Create
        </ButtonV2>
      </LabraToolTipV2>
    ) : (
      <MuiTooltip title={!canCreatePrivateOffer ? permissionMessageString : ''}>
        <span>
          <DropdownButtonV2
            variant="contained"
            styleType="primary"
            buttonText={'Create'}
            data-testid={'create-private-offer'}
            disabled={!canCreatePrivateOffer}
            options={[
              {
                label: 'Create new private offer',
                route: '',
                key: 'offer',
                disabledTooltip:
                  'Cannot create offers for restricted products.',
              },
              {
                label: 'Create agreement based offer',
                route: '',
                key: 'subscription',
                disable:
                  selectedCloudTypeProductListing !== 'AWS' ||
                  selectedProductDetails.status ===
                    ProductStatus.PRODUCT_STATUS_RESTRICTED,
                disabledTooltip:
                  selectedProductDetails.status ===
                  ProductStatus.PRODUCT_STATUS_RESTRICTED
                    ? 'Cannot create ABO for restricted products.'
                    : 'Coming soon',
              },
            ]}
            onOptionClick={handleRoute}
          />
        </span>
      </MuiTooltip>
    )

  const handleCreatePrivateOffer = () => {
    handleClosePrivateOfferModal()
    const pathname = externalBaseURL
      ? routeCloud && currentAccountId
        ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${currentAccountId}/product-listing/${selectedProductId}/private-offers/edit${search ? search : ''}`
        : `${externalBaseURL}/${selectedProductId}/private-offers/edit`
      : routeCloud && currentAccountId
        ? `/flyout/${routeCloud}/${currentAccountId}/products-listing/${selectedProductId}/private-offers/edit`
        : `/flyout/products-listing/${selectedProductId}/private-offers/edit`

    navigate({
      pathname: externalBaseURL
        ? routeCloud && currentAccountId
          ? `/external-apps/hubspot/v1/${secureKeyFromURL}/partners/${partnerIdFromURL}/cloud/${routeCloud}/accounts/${currentAccountId}/product-listing/${selectedProductId}/private-offers/edit`
          : `${externalBaseURL}/${selectedProductId}/private-offers/edit`
        : routeCloud && currentAccountId
          ? `/flyout/${routeCloud}/${currentAccountId}/products-listing/${selectedProductId}/private-offers/edit`
          : `/flyout/products-listing/${selectedProductId}/private-offers/edit`,
      search: search,
    })
  }

  if (productsLoading) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingLogo />
      </div>
    )
  }

  return (
    <>
      {products && !products?.length && !loading && !externalBaseURL ? (
        <ListingCreationPage />
      ) : (
        <>
          <div
            className={clsx(isAdmin ? classes.adminContainer : '')}
          >
            {!isAdmin && (
              <div className={classes.upperContainter}>
                {selectedCloudTypeProductListing === 'AWS' &&
                !externalBaseURL ? (
                  <SearchBoxComponent
                    options={searchDropDownOptions.privateOfferoptions}
                    handleSearchClick={handleSearchClick}
                    loading={false}
                    searchNow={searchNow}
                    setSearchNow={setSearchNow}
                    showCrossMark={showCrossMark}
                    setShowCrossMark={setShowCrossMark}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    selectedOption={selectedOption}
                    setSelectedOption={setSelectedOption}
                  />
                ) : (
                  <></>
                )}
                <div
                  className={clsx(
                    classes.createOfferBtn,
                    classes.alignButton,
                    classes.dropdownAndProduct
                  )}
                >
                  {
                    <div style={{ width: '220px', height: '44px' }}>
                      <CloudAccountChangeV2
                        cloudAccounts={marketplaceCloudAccounts}
                        selectedAccountId={selectedAccountIdWithCloud}
                        onChange={handleCloudAccountChange}
                        width={220}
                        size="small"
                      />
                    </div>
                  }
                  <div className={classes.dropDownWidth}>
                    <ProductSelectorDropdown
                      functionType={FlyoutFunction.PRIVATE_OFFERS}
                      externalBaseURL={externalBaseURL}
                      search={search}
                    />
                  </div>
                  {createButtonWithSubscription}
                  {(selectedCloudTypeProductListing === 'AWS' ||
                    selectedCloudTypeProductListing === 'AZURE') &&
                  !externalBaseURL ? (
                    <>
                      <div className={classes.filterComponent}>
                        <Filters
                          sections={sections}
                          applyFilters={applyFilters}
                          minWidth="575px"
                        />
                      </div>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            )}

            {(selectedCloudTypeProductListing === 'AWS' ||
              selectedCloudTypeProductListing === 'AZURE') && (
              <div
                className={clsx(
                  classes.filtersAppliedContainer,
                  appliedFilteredValues?.length ? classes.appliedFilter : ''
                )}
                data-testid="filters-applied-component"
              >
                <FiltersApplied
                  selectedValues={appliedFilteredValues}
                  onDelete={onDeleteFiltersAppliedChip}
                  chipDisplayLimit={5}
                  noBorder
                />
              </div>
            )}

            {searchNow && selectedCloudTypeProductListing === 'AWS' ? (
              <>
                <SearchResultsCount totalCount={totalCount} loading={loading} />
                {totalCount > 0 && (
                  <PrivateOffersV2
                    inputValue={inputValue}
                    selectedOption={selectedOption}
                    crmOpportunityId={crmOpportunityId}
                    expiringInDateRange={expiryDate}
                    privateOfferStatus={selectedPrivateOfferStatusCheckboxes}
                    privateOffersForTable={privateOffersForTable}
                    selectedPrivateOffer={
                      selectedPrivateOffer?.offer as DetailedOfferPreviewData
                    }
                    planType={selectedPrivateOffer?.planType as PlanType}
                    openModal={openModal}
                    handleModalClose={handleClose}
                    selectedCloudTypeProductListing={
                      selectedCloudTypeProductListing
                    }
                    externalBaseURL={externalBaseURL}
                    partnerId={currentAccountId}
                    products={products}
                    tableEl={tableEl}
                    searchApplied={true}
                    appliedFilteredValues={appliedFilteredValues}
                    DetailedOfferPreviewComponent={DetailedOffer}
                    trackRowAction={trackRowAction}
                  />
                )}
              </>
            ) : (
              <PrivateOffersV2
                inputValue={inputValue}
                selectedOption={selectedOption}
                crmOpportunityId={crmOpportunityId}
                expiringInDateRange={expiryDate}
                privateOfferStatus={selectedPrivateOfferStatusCheckboxes}
                privateOffersForTable={privateOffersForTable}
                selectedPrivateOffer={
                  selectedPrivateOffer?.offer as DetailedOfferPreviewData
                }
                planType={selectedPrivateOffer?.planType as PlanType}
                openModal={openModal}
                handleModalClose={handleClose}
                selectedCloudTypeProductListing={
                  selectedCloudTypeProductListing
                }
                externalBaseURL={externalBaseURL}
                partnerId={currentAccountId}
                products={products}
                tableEl={tableEl}
                appliedFilteredValues={appliedFilteredValues}
                DetailedOfferPreviewComponent={DetailedOffer}
                trackRowAction={trackRowAction}
              />
            )}
          </div>
          <div data-testid="modal-po">
            <ModalToCreatePrivateOffer
              open={openNewPrivateOfferModal}
              onClose={handleClosePrivateOfferModal}
              handleCreatePrivateOffer={handleCreatePrivateOffer}
              handleProductChange={handleProductChange}
              selectedAccountIdWithCloud={selectedAccountIdWithCloud}
              handleCloudAccountChange={handleCloudAccountChange}
              hubspotCloudSelector={
                externalBaseURL ? (
                  <div>
                    <CloudChangeV2
                      setInputValue={setInputValue}
                      setShowCrossMark={setShowCrossMark}
                      setSearchNow={setSearchNow}
                    />
                  </div>
                ) : (
                  <></>
                )
              }
              externalBaseUrl={externalBaseURL}
            />
          </div>
        </>
      )}
      {isModalOpen && (
        <SubscriptionSelectionModal
          open={isModalOpen}
          handleModalClose={handleModalClose}
          acceptSelectedSubscription={acceptSelectedSubscription}
          currentSelectedSubscriptionId={selectedSubscription}
          acceptLoading={loadingActiveResellerAuth}
          type={'MODAL'}
          offerType={'PUBLIC_PRIVATE'}
        />
      )}
      <ChannelOfferCreationModal
        open={cppoSubscription}
        setOpen={setCppoSubscription}
        handleNext={handleNext}
        subscriptionId={selectedSubscription}
      />
    </>
  )
}

export default PrivateOffersWrapperUIV2
